import React from "react";
import PropTypes from "prop-types";
import { Box, Chip, makeStyles } from "@material-ui/core";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  dimensionLabel: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    fontWeight: 500,
    marginRight: theme.spacing(0.5),
    whiteSpace: "nowrap",
    paddingTop: "2px", // Align with chips
  },
  chip: {
    height: 24,
    fontSize: "0.75rem",
    backgroundColor: (props) => props.clientColor,
    color: "#fff",
    "& .MuiChip-deleteIcon": {
      color: "rgba(255, 255, 255, 0.7)",
      fontSize: "1rem",
      "&:hover": {
        color: "#fff",
      },
    },
  },
  filterGroup: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    flexWrap: "wrap",
  },
}));

export default function FilterChipBar({
  filters,
  dimensionLabels,
  onRemoveFilter,
  onClearDimension,
  clientColor,
}) {
  const classes = useStyles({ clientColor });
  const { t } = useTranslation("translation", {
    keyPrefix: "rgm.cockpit.filters.chip_bar",
  });

  // Get dimensions that have active filters
  const activeDimensions = Object.keys(filters).filter(
    (key) => filters[key] && filters[key].length > 0,
  );

  if (activeDimensions.length === 0) {
    return null;
  }

  // Simple static limit: show max 2 dimensions to keep within 2 lines
  // This prevents the feedback loop from dynamic height detection
  const MAX_VISIBLE_DIMENSIONS = 2;
  const visibleDimensions = activeDimensions.slice(0, MAX_VISIBLE_DIMENSIONS);
  const hiddenDimensionsCount =
    activeDimensions.length - MAX_VISIBLE_DIMENSIONS;

  return (
    <Box className={classes.container}>
      {visibleDimensions.map((dimensionKey) => {
        const values = filters[dimensionKey];
        const label = dimensionLabels[dimensionKey] || dimensionKey;
        const shouldCondense = values.length > 5;

        return (
          <Box key={dimensionKey} className={classes.filterGroup}>
            <span className={classes.dimensionLabel}>{label}:</span>
            {shouldCondense ? (
              // Show condensed chip when more than 5 filters
              <Chip
                key={`${dimensionKey}-condensed`}
                label={`${values.length} ${t("filters_selected")}`}
                size="small"
                className={classes.chip}
                onDelete={() => onClearDimension(dimensionKey)}
              />
            ) : (
              // Show individual chips when 5 or fewer
              <>
                {values.map((value) => (
                  <Chip
                    key={`${dimensionKey}-${value}`}
                    label={value || t("blank")}
                    size="small"
                    className={classes.chip}
                    onDelete={() => onRemoveFilter(dimensionKey, value)}
                  />
                ))}
                {values.length > 1 && (
                  <Chip
                    label={t("clear_all", { label })}
                    size="small"
                    variant="outlined"
                    style={{
                      borderColor: clientColor,
                      color: clientColor,
                      fontSize: "0.7rem",
                      height: 20,
                    }}
                    onDelete={() => onClearDimension(dimensionKey)}
                  />
                )}
              </>
            )}
          </Box>
        );
      })}

      {/* Show "and N more" chip if there are hidden dimensions */}
      {hiddenDimensionsCount > 0 && (
        <Chip
          label={t(
            hiddenDimensionsCount === 1 ? "and_more" : "and_more_plural",
            { count: hiddenDimensionsCount },
          )}
          size="small"
          style={{
            backgroundColor: "#f0f0f0",
            color: clientColor,
            fontSize: "0.75rem",
            height: 24,
            fontWeight: 500,
          }}
        />
      )}
    </Box>
  );
}

FilterChipBar.propTypes = {
  filters: PropTypes.object.isRequired,
  dimensionLabels: PropTypes.object.isRequired,
  onRemoveFilter: PropTypes.func.isRequired,
  onClearDimension: PropTypes.func.isRequired,
  clientColor: PropTypes.string.isRequired,
};
